import { type Booking } from "@/hooks/useBookings";
import { type Property } from "@/types/property";
import BookingBar from "./BookingBar";
import { Virtualizer } from "@tanstack/react-virtual";

interface CalendarGridRowsProps {
  properties: Property[];
  days: Date[];
  rowHeight: number;
  cellWidth: number;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  bookingsByProperty: Record<string, Booking[]>;
  getDayStatus: (
    day: Date,
    propertyId: string,
  ) => {
    isPast: boolean;
    isBlocked: boolean;
    isBooked: boolean;
    canClick: boolean;
  };
  visibleRange: { start: number; end: number };
  onDayClick: (day: Date, property: Property) => void;
  onBookingClick?: (booking: Booking) => void;
}

const CalendarGridRows = ({
  properties,
  days,
  rowHeight,
  cellWidth,
  rowVirtualizer,
  columnVirtualizer,
  bookingsByProperty,
  getDayStatus,
  visibleRange,
  onDayClick,
  onBookingClick,
}: CalendarGridRowsProps) => (
  <>
    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
      const row = properties[virtualRow.index];

      return (
        <div
          key={`${row.id}-${virtualRow.index}`}
          className="flex bg-white border-t absolute"
          style={{
            top: virtualRow.start + rowHeight * 2,
            height: rowHeight,
            width: columnVirtualizer.getTotalSize(),
          }}
        >
          {columnVirtualizer.getVirtualItems().map((column) => {
            const date = days[column.index];

            const { isPast, isBlocked, isBooked, canClick } = getDayStatus(
              date,
              row.id,
            );

            const bgClass = isBlocked
              ? "bg-gray-200"
              : isPast
              ? "bg-gray-100"
              : "bg-white";

            const hoverClass = canClick
              ? "hover:bg-blue-50 cursor-pointer"
              : "cursor-not-allowed";

            return (
              <div
                data-index={column.index}
                key={column.key}
                style={{
                  position: "absolute",
                  left: column.start,
                  width: column.size,
                  height: rowHeight,
                }}
                className={`border-r border-b px-1 relative transition-all ${bgClass} ${hoverClass}`}
                onClick={() => onDayClick(date, row)}
              >
                {isBlocked && !isBooked && (
                  <div className="absolute inset-0 pointer-events-none bg-[repeating-linear-gradient(135deg,_rgba(156,163,175,0.3)_0_10%,_transparent_10%_20%)] opacity-80" />
                )}
              </div>
            );
          })}

          {(bookingsByProperty[row.id] || []).map((booking) => (
            <BookingBar
              key={booking.id}
              booking={booking}
              days={days}
              cellWidth={cellWidth}
              rowHeight={rowHeight}
              visibleRange={visibleRange}
              onClick={onBookingClick}
            />
          ))}
        </div>
      );
    })}
  </>
);

export default CalendarGridRows;
